import React, { useEffect, useState, useCallback } from "react";
import { ENDPOINTS, authFetch } from "../../api/api";
import "../components/AdminLayout.css";

const useToast = () => {
  const [toast, setToast] = useState(null);
  const show = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);
  return { toast, show };
};

// ── Reusable section wrapper ────────────────────────────────────────────────
const Section = ({ title, icon, children }) => (
  <div className="adm-card" style={{ marginBottom: "1.5rem" }}>
    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1.25rem", paddingBottom: "0.875rem", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
      <i className={icon} style={{ color: "#0a9b7c", fontSize: "0.9rem" }}></i>
      <h3 style={{ fontFamily: "'Syne',sans-serif", fontSize: "0.95rem", fontWeight: 700, color: "var(--adm-text)" }}>{title}</h3>
    </div>
    {children}
  </div>
);

// ── Hero Content editor ─────────────────────────────────────────────────────
const HeroEditor = () => {
  const { toast, show } = useToast();
  const [hero, setHero] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    authFetch(ENDPOINTS.hero).then(setHero).catch(() => setHero({}));
  }, []);

  const handleChange = (e) =>
    setHero((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (hero?.id) {
        await authFetch(`${ENDPOINTS.hero}/${hero.id}`, { method: "PATCH", body: JSON.stringify(hero) });
      } else {
        const created = await authFetch(ENDPOINTS.hero, { method: "POST", body: JSON.stringify(hero) });
        setHero(created);
      }
      show("Hero content saved!");
    } catch (err) {
      show(err.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!hero) return <div className="adm-loading"><div className="adm-loading-spinner"></div></div>;

  return (
    <>
      <div className="adm-form">
        <div className="adm-form-field">
          <label className="adm-form-label">Eyebrow text (small label above heading)</label>
          <input name="eyebrow" className="adm-form-input" value={hero.eyebrow || ""}
            onChange={handleChange} placeholder="Software Agency · Nairobi, Kenya" />
        </div>
        <div className="adm-form-field">
          <label className="adm-form-label">Main Heading</label>
          <input name="heading" className="adm-form-input" value={hero.heading || ""}
            onChange={handleChange} placeholder="We Build Technology That Solves Real Problems" />
        </div>
        <div className="adm-form-field">
          <label className="adm-form-label">Subtitle / Paragraph</label>
          <textarea name="subtitle" className="adm-form-textarea" value={hero.subtitle || ""}
            onChange={handleChange} placeholder="We are Solviotech — a passionate team..." />
        </div>
        <div className="adm-form-row">
          <div className="adm-form-field">
            <label className="adm-form-label">Primary Button Text</label>
            <input name="primaryBtn" className="adm-form-input" value={hero.primaryBtn || ""}
              onChange={handleChange} placeholder="View Our Work" />
          </div>
          <div className="adm-form-field">
            <label className="adm-form-label">Secondary Button Text</label>
            <input name="secondaryBtn" className="adm-form-input" value={hero.secondaryBtn || ""}
              onChange={handleChange} placeholder="Get In Touch" />
          </div>
        </div>
        <div className="adm-form-field">
          <label className="adm-form-label">Background Image URL</label>
          <input name="image" className="adm-form-input" value={hero.image || ""}
            onChange={handleChange} placeholder="/images/homepage.jpg" />
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
        <button className="adm-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : <><i className="fa-solid fa-floppy-disk"></i> Save Hero</>}
        </button>
      </div>
      {toast && (
        <div className={`adm-toast adm-toast--${toast.type}`}>
          <i className={`fa-solid ${toast.type === "success" ? "fa-check" : "fa-xmark"}`}></i>
          {toast.msg}
        </div>
      )}
    </>
  );
};

// ── Stats editor ────────────────────────────────────────────────────────────
const StatsEditor = () => {
  const { toast, show } = useToast();
  const [stats, setStats] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    authFetch(ENDPOINTS.stats).then(setStats).catch(() => setStats([]));
  }, []);

  const updateStat = (i, field, value) =>
    setStats((p) => p.map((s, idx) => idx === i ? { ...s, [field]: value } : s));

  const addStat = () =>
    setStats((p) => [...p, { icon: "fa-solid fa-chart-line", value: 0, suffix: "+", label: "New Stat", order: p.length }]);

  const saveStat = async (stat) => {
    setSaving(true);
    try {
      if (stat.id) {
        await authFetch(`${ENDPOINTS.stats}/${stat.id}`, { method: "PATCH", body: JSON.stringify({ ...stat, value: Number(stat.value) }) });
      } else {
        const created = await authFetch(ENDPOINTS.stats, { method: "POST", body: JSON.stringify({ ...stat, value: Number(stat.value) }) });
        setStats((p) => p.map((s) => s === stat ? created : s));
      }
      show("Stat saved!");
    } catch (err) {
      show(err.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        {stats.map((s, i) => (
          <div key={s.id || i} style={{
            display: "grid", gridTemplateColumns: "1fr 80px 60px 1fr auto",
            gap: "0.5rem", alignItems: "center",
            padding: "0.75rem", background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.05)", borderRadius: 8,
          }}>
            <input className="adm-form-input" value={s.label} onChange={(e) => updateStat(i, "label", e.target.value)} placeholder="Label" style={{ fontSize: "0.8rem" }} />
            <input className="adm-form-input" value={s.value} onChange={(e) => updateStat(i, "value", e.target.value)} placeholder="50" type="number" style={{ fontSize: "0.8rem" }} />
            <input className="adm-form-input" value={s.suffix} onChange={(e) => updateStat(i, "suffix", e.target.value)} placeholder="+" style={{ fontSize: "0.8rem" }} />
            <input className="adm-form-input" value={s.icon} onChange={(e) => updateStat(i, "icon", e.target.value)} placeholder="fa-solid fa-rocket" style={{ fontSize: "0.8rem" }} />
            <button className="adm-save-btn" onClick={() => saveStat(s)} disabled={saving} style={{ whiteSpace: "nowrap", fontSize: "0.75rem", padding: "0.45rem 0.7rem" }}>
              Save
            </button>
          </div>
        ))}
      </div>
      <button onClick={addStat} style={{
        marginTop: "0.75rem", background: "rgba(10,107,90,0.1)", border: "1px dashed rgba(10,107,90,0.3)",
        borderRadius: 8, padding: "0.5rem 1rem", color: "#0a9b7c", fontSize: "0.8rem",
        cursor: "pointer", width: "100%",
      }}>
        <i className="fa-solid fa-plus" style={{ marginRight: "0.4rem" }}></i>Add Stat
      </button>
      {toast && (
        <div className={`adm-toast adm-toast--${toast.type}`}>
          <i className={`fa-solid ${toast.type === "success" ? "fa-check" : "fa-xmark"}`}></i>
          {toast.msg}
        </div>
      )}
    </>
  );
};

// ── About editor ────────────────────────────────────────────────────────────
const AboutEditor = () => {
  const { toast, show } = useToast();
  const [about, setAbout] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    authFetch(ENDPOINTS.about).then(setAbout).catch(() => setAbout({}));
  }, []);

  const handleChange = (e) =>
    setAbout((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (about?.id) {
        await authFetch(`${ENDPOINTS.about}/${about.id}`, { method: "PATCH", body: JSON.stringify(about) });
      } else {
        const created = await authFetch(ENDPOINTS.about, { method: "POST", body: JSON.stringify(about) });
        setAbout(created);
      }
      show("About content saved!");
    } catch (err) {
      show(err.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!about) return <div className="adm-loading"><div className="adm-loading-spinner"></div></div>;

  return (
    <>
      <div className="adm-form">
        <div className="adm-form-field">
          <label className="adm-form-label">Main Heading</label>
          <input name="heading" className="adm-form-input" value={about.heading || ""}
            onChange={handleChange} placeholder="We Build Technology That Matters" />
        </div>
        <div className="adm-form-field">
          <label className="adm-form-label">Subheading</label>
          <input name="subheading" className="adm-form-input" value={about.subheading || ""}
            onChange={handleChange} placeholder="A Nairobi-based software agency..." />
        </div>
        <div className="adm-form-field">
          <label className="adm-form-label">Body Text</label>
          <textarea name="body" className="adm-form-textarea" value={about.body || ""}
            onChange={handleChange} placeholder="Tell your story here..." style={{ minHeight: 120 }} />
        </div>
        <div className="adm-form-field">
          <label className="adm-form-label">Image URL</label>
          <input name="image" className="adm-form-input" value={about.image || ""}
            onChange={handleChange} placeholder="/images/about.jpg" />
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
        <button className="adm-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : <><i className="fa-solid fa-floppy-disk"></i> Save About</>}
        </button>
      </div>
      {toast && (
        <div className={`adm-toast adm-toast--${toast.type}`}>
          <i className={`fa-solid ${toast.type === "success" ? "fa-check" : "fa-xmark"}`}></i>
          {toast.msg}
        </div>
      )}
    </>
  );
};

// ── Main AdminContent page ──────────────────────────────────────────────────
const AdminContent = () => {
  const [tab, setTab] = useState("hero");

  const TABS = [
    { id: "hero",  label: "Hero Section",  icon: "fa-solid fa-house" },
    { id: "stats", label: "Site Stats",    icon: "fa-solid fa-chart-bar" },
    { id: "about", label: "About Page",   icon: "fa-solid fa-building" },
  ];

  return (
    <div>
      <div className="adm-page-header">
        <div>
          <h1 className="adm-page-title">Site Content</h1>
          <p className="adm-page-sub">Edit homepage and about page content</p>
        </div>
      </div>

      {/* Tab switcher */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem", borderBottom: "1px solid rgba(255,255,255,0.06)", paddingBottom: "0" }}>
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            display: "flex", alignItems: "center", gap: "0.4rem",
            padding: "0.6rem 1rem", background: "none", border: "none", cursor: "pointer",
            fontSize: "0.875rem", fontWeight: 500, transition: "all 0.2s",
            color: tab === t.id ? "#0a9b7c" : "rgba(255,255,255,0.4)",
            borderBottom: `2px solid ${tab === t.id ? "#0a9b7c" : "transparent"}`,
            marginBottom: "-1px",
          }}>
            <i className={t.icon} style={{ fontSize: "0.8rem" }}></i>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "hero"  && <Section title="Hero Section"   icon="fa-solid fa-house"><HeroEditor /></Section>}
      {tab === "stats" && <Section title="Homepage Stats" icon="fa-solid fa-chart-bar"><StatsEditor /></Section>}
      {tab === "about" && <Section title="About Page"     icon="fa-solid fa-building"><AboutEditor /></Section>}
    </div>
  );
};

export default AdminContent;
